import React, { createElement } from "react";

import {
  AttachFile,
  CameraAlt,
  CloudUpload,
  Folder,
  InsertDriveFile,
} from "@mui/icons-material";
import {
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { postSubmissionAttachmentExportStatus } from "@rubi/api-generated";
import { colors } from "@rubi/ui";
import { toast } from "@rubi/ui";
import axios from "axios";
import ReactDOMServer from "react-dom/server";
import { ElementFactory, Question, Serializer, SvgRegistry } from "survey-core";
import { localization } from "survey-creator-core";
import { PropertyGridEditorCollection } from "survey-creator-react";
import {
  ReactQuestionFactory,
  SurveyQuestionElementBase,
} from "survey-react-ui";

import AttachmentPreviewModal from "@/common/AttachmentPreviewModal";
import { tQuestion } from "@/components/online-reporting/custom-questions/i18nQuestion";

import { isDirectoryUpload, processDirectory } from "./directoryUtils";
import { ProgressBar } from "./ProgressBar";

interface FileWithPreview extends File {
  preview?: string;
}

interface SurveyJSFileObject {
  content?: string;
  file?: File;
  id?: string;
  name?: string;
  type?: string;
  submission_id?: string;
  include_in_export?: boolean;
  thumbnail_url?: string | null;
}

const ENHANCED_FILE_UPLOAD_TYPE = "enhanced-file-upload";

export interface UploadProgress {
  fileName: string;
  progress: number;
  status: "processing" | "uploading" | "completed" | "error" | "downloading";
  error?: string;
}

// A model for the new question type
export class QuestionEnhancedFileUploadModel extends Question {
  getType() {
    return ENHANCED_FILE_UPLOAD_TYPE;
  }

  get allowDirectoryUpload() {
    return this.getPropertyValue("allowDirectoryUpload");
  }

  set allowDirectoryUpload(val: boolean) {
    this.setPropertyValue("allowDirectoryUpload", val);
  }

  get acceptedTypes() {
    return this.getPropertyValue("acceptedTypes");
  }

  set acceptedTypes(val: string) {
    this.setPropertyValue("acceptedTypes", val);
  }

  get maxSize() {
    return this.getPropertyValue("maxSize");
  }

  set maxSize(val: number) {
    this.setPropertyValue("maxSize", val);
  }
}

// Register the question model
// Guard against duplicate registration (can happen with HMR or multiple imports)
export function registerEnhancedFileUpload() {
  if (
    !ElementFactory.Instance.getAllTypes().includes(ENHANCED_FILE_UPLOAD_TYPE)
  ) {
    ElementFactory.Instance.registerElement(
      ENHANCED_FILE_UPLOAD_TYPE,
      (name) => {
        return new QuestionEnhancedFileUploadModel(name);
      },
    );
  }
}

// Specify display names for the question type and its properties
// i18n: builder label deferred
const locale = localization.getLocale("");
locale.qt[ENHANCED_FILE_UPLOAD_TYPE] = "Enhanced File Upload";
locale.pe.enhancedFileUploadType = "Enhanced File Upload type";

// Register an SVG icon for the question type
const SURVEY_ICON_COLOR = colors.text.disabled;
const svg = ReactDOMServer.renderToString(
  <AttachFile style={{ fill: SURVEY_ICON_COLOR }} />,
);
SvgRegistry.registerIconFromSvg(ENHANCED_FILE_UPLOAD_TYPE, svg);

// Add question type metadata for further serialization into JSON
// Guard against duplicate registration (can happen with HMR or multiple imports)
if (!Serializer.findClass(ENHANCED_FILE_UPLOAD_TYPE)) {
  Serializer.addClass(
    ENHANCED_FILE_UPLOAD_TYPE,
    [
      {
        category: "general",
        choices: [false, true],
        default: true,
        name: "allowDirectoryUpload",
        visibleIndex: 2,
      },
      {
        category: "general",
        name: "acceptedTypes",
        visibleIndex: 3,
      },
      {
        category: "general",
        name: "maxSize",
        visibleIndex: 4,
      },
    ],
    function () {
      return new QuestionEnhancedFileUploadModel("");
    },
    "question",
  );
}

// A class that renders questions of the new type in the UI
export class SurveyQuestionEnhancedFileUpload extends SurveyQuestionElementBase {
  private fileInputRef = React.createRef<HTMLInputElement>();
  private directoryInputRef = React.createRef<HTMLInputElement>();
  private cameraInputRef = React.createRef<HTMLInputElement>();

  constructor(props: any) {
    super(props);
    this.state = {
      uploadProgress: [] as UploadProgress[],
      downloadingFiles: new Set<string>(),
      previewItem: null as (FileWithPreview | SurveyJSFileObject) | null,
    };
  }

  get question(): QuestionEnhancedFileUploadModel {
    return this.questionBase as QuestionEnhancedFileUploadModel;
  }

  get value() {
    const val = this.question.value || [];
    // SurveyJS may store files as objects with file/content/id properties
    // or as File objects directly - normalize to always include file info
    return val;
  }

  // Helper to extract file info from either File object or SurveyJS file object
  getFileInfo = (
    item: FileWithPreview | SurveyJSFileObject,
  ): FileWithPreview | null => {
    // If it's already a File object
    if (item instanceof File) {
      return item as FileWithPreview;
    }
    // If it's a SurveyJS file object with a file property
    const surveyFile = item as SurveyJSFileObject;
    if (surveyFile.file) {
      const file = surveyFile.file as FileWithPreview;
      // If there's content (base64 preview), add it to the file
      if (surveyFile.content && !file.preview) {
        file.preview = surveyFile.content;
      }
      // If surveyFile.name exists and is different from file.name, preserve it
      // Note: File.name is read-only, but we can add a custom property for display
      if (surveyFile.name && surveyFile.name !== file.name) {
        // Store the preferred name in a custom property for later retrieval
        (file as any).preferredName = surveyFile.name;
      }
      return file;
    }
    // If it has content (base64) but no file object, try to reconstruct
    if (surveyFile.content) {
      // Try to get name from surveyFile or create one
      const fileObj = surveyFile.file as File | undefined;
      const name =
        surveyFile.name || (fileObj ? fileObj.name : undefined) || "file";
      // Create a pseudo-file object for display
      const blob = surveyFile.content.startsWith("data:")
        ? this.dataURLtoBlob(surveyFile.content)
        : null;
      if (blob) {
        const file = new File([blob], name, {
          type:
            surveyFile.type ||
            (fileObj ? fileObj.type : undefined) ||
            "application/octet-stream",
        });
        const fileWithPreview = file as FileWithPreview;
        fileWithPreview.preview = surveyFile.content;
        return fileWithPreview;
      }
    }
    // If we have a name but no file, create a minimal file object for display
    const fileObj = surveyFile.file as File | undefined;
    if (surveyFile.name || (fileObj && fileObj.name)) {
      const name =
        surveyFile.name || (fileObj ? fileObj.name : undefined) || "file";
      const file = new File([], name, {
        type:
          surveyFile.type ||
          (fileObj ? fileObj.type : undefined) ||
          "application/octet-stream",
      });
      const fileWithPreview = file as FileWithPreview;
      if (surveyFile.content) {
        fileWithPreview.preview = surveyFile.content;
      }
      return fileWithPreview;
    }
    return null;
  };

  dataURLtoBlob = (dataURL: string): Blob | null => {
    try {
      const arr = dataURL.split(",");
      const mime = arr[0].match(/:(.*?);/)?.[1] || "application/octet-stream";
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      return new Blob([u8arr], { type: mime });
    } catch {
      return null;
    }
  };

  // Convert file to base64 for image preview
  toBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
    });

  handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    // Get existing files to append new ones
    // Need to preserve existing files that are already uploaded (SurveyJS format)
    const existingFiles = (this.value || []) as (
      | FileWithPreview
      | SurveyJSFileObject
    )[];

    // Check if this is a directory upload
    const isDirectory = isDirectoryUpload(files);

    let filesToUpload: File[] = [];

    if (isDirectory && this.question.allowDirectoryUpload) {
      // Process directory: calculate checksums, create zip
      // Get the directory name for progress tracking
      let directoryName = "directory";
      if (files.length > 0 && files[0].webkitRelativePath) {
        const firstPath = files[0].webkitRelativePath;
        const parts = firstPath.split("/");
        if (parts.length > 0 && parts[0]) {
          directoryName = parts[0].replace(/[<>:"/\\|?*]/g, "_");
        }
      }
      const zipFileName = `${directoryName}.zip`;

      // Initialize progress tracking for directory processing
      this.updateProgress(zipFileName, 0, "processing");

      try {
        const processed = await processDirectory(files, (progress) => {
          // Update progress during ZIP processing
          this.updateProgress(zipFileName, progress, "processing");
        });
        // Create zip file with the directory name as the filename
        const zipFile = new File([processed.zip], processed.zipFileName, {
          type: "application/zip",
        });
        // Add preview property to preserve the filename
        const zipFileWithPreview = zipFile as FileWithPreview;
        zipFileWithPreview.preview = ""; // No preview for zip files
        filesToUpload = [zipFileWithPreview];

        // Update progress for directory processing completion
        this.updateProgress(processed.zipFileName, 100, "processing");
      } catch (error: any) {
        const errorMsg = tQuestion("enhancedFileUpload.directoryError", {
          message: error.message,
        });
        this.updateProgress(zipFileName, 0, "error", errorMsg);
        return;
      }
    } else {
      // Regular file upload - add previews for images
      const filesWithPreviews = await Promise.all(
        Array.from(files).map(async (file) => {
          const fileWithPreview = file as FileWithPreview;
          // Generate preview for images
          if (
            file.type.startsWith("image/") &&
            !file.type.includes("heic") &&
            !file.type.includes("heif") &&
            !file.name.toLowerCase().endsWith(".heic") &&
            !file.name.toLowerCase().endsWith(".heif")
          ) {
            try {
              fileWithPreview.preview = await this.toBase64(file);
            } catch (error) {
              console.error("Error generating preview:", error);
            }
          }
          return fileWithPreview;
        }),
      );
      filesToUpload = filesWithPreviews;
    }

    // Store new files in question value (will be merged with existing after upload)
    // We store them separately so we can track which ones are new
    const existingFileObjects = existingFiles.filter(
      (item) => !(item instanceof File),
    ) as SurveyJSFileObject[];
    const existingFileFiles = existingFiles.filter(
      (item) => item instanceof File,
    ) as FileWithPreview[];

    // Combine: existing uploaded files (SurveyJS objects) + existing File objects + new files
    const allFiles = [
      ...existingFileObjects,
      ...existingFileFiles,
      ...filesToUpload,
    ];

    // Store all files in question value
    this.question.value = allFiles;

    // Initialize progress tracking for new files only (don't overwrite existing progress)
    filesToUpload.forEach((file) => {
      const existingProgress = (this.state.uploadProgress || []).find(
        (p: UploadProgress) => p.fileName === file.name,
      );
      // Only set progress if this file doesn't already have progress tracking
      if (!existingProgress) {
        this.updateProgress(file.name, 0, "uploading");
      }
    });

    // Trigger SurveyJS's onUploadFiles event handlers
    // SurveyJS file questions automatically trigger onUploadFiles when files are selected
    // For our custom question, we need to manually trigger the upload
    if (this.question.survey) {
      const uploadOptions = {
        callback: (returnValues: any[], errorValues: string[]) => {
          // Handle upload completion
          if (errorValues.length > 0) {
            errorValues.forEach((error, index) => {
              this.updateProgress(
                filesToUpload[index]?.name || "file",
                0,
                "error",
                error,
              );
            });
          } else {
            // Update question value with returnValues (which contain {file, content, id})
            const currentValue = (this.question.value || []) as any[];

            filesToUpload.forEach((file, index) => {
              if (returnValues[index]) {
                const returnValue = returnValues[index];
                // Get the actual filename from returnValue, preserving the original file name
                // This is important for zip files which should keep their directory name
                // Priority: returnValue.file.name (from upload) > file.name (original) > returnValue.name
                const actualFileName =
                  (returnValue.file instanceof File && returnValue.file.name) ||
                  returnValue.file?.name ||
                  file.name ||
                  returnValue.name ||
                  tQuestion("enhancedFileUpload.unknownFile");

                // Ensure the returnValue has the correct name property for display
                if (!returnValue.name && actualFileName) {
                  returnValue.name = actualFileName;
                }
                // Also ensure the file object has the correct name if it exists
                if (
                  returnValue.file &&
                  actualFileName &&
                  returnValue.file.name !== actualFileName
                ) {
                  // If returnValue.file is a File object, we can't change its name property
                  // but we ensure returnValue.name is set for display purposes
                  if (!returnValue.name) {
                    returnValue.name = actualFileName;
                  }
                }

                // Mark as completed
                this.updateProgress(actualFileName, 100, "completed");

                // Find and replace the File object with the returnValue object
                // This preserves existing uploaded files and only updates the ones that just finished
                const existingIndex = currentValue.findIndex(
                  (item: any) =>
                    (item instanceof File && item.name === file.name) ||
                    (item instanceof File && item.name === actualFileName) ||
                    (item?.file instanceof File &&
                      item.file.name === file.name) ||
                    (item?.file instanceof File &&
                      item.file.name === actualFileName) ||
                    item?.file?.name === file.name ||
                    item?.file?.name === actualFileName ||
                    item?.name === file.name ||
                    item?.name === actualFileName,
                );
                if (existingIndex >= 0) {
                  currentValue[existingIndex] = returnValue;
                } else {
                  // Add new file if not found (shouldn't happen, but safe)
                  currentValue.push(returnValue);
                }
              }
            });

            // Update question value and force re-render to show all files including new ones
            this.question.value = currentValue;
            this.setState({});
          }
        },
        files: filesToUpload,
        name: this.question.name,
        onProgress: (fileName: string, progress: number) => {
          // Update progress for the specific file
          this.updateProgress(fileName, progress, "uploading");
          // Force re-render to show updated progress
          this.setState({});
        },
        question: this.question,
      };

      // Get the upload handler from the survey's event system
      // The handler is registered in ORReportCompletionPage via survey.onUploadFiles.add
      const survey = this.question.survey as any;
      if (survey && survey.onUploadFiles) {
        // Try multiple methods to trigger the upload handler
        // Method 1: Access handlers array directly (SurveyJS Event class)
        const eventHandlers = (survey.onUploadFiles as any)?.handlers || [];
        if (eventHandlers.length > 0) {
          // Call each handler - SurveyJS handlers expect (survey, options)
          eventHandlers.forEach(
            (handler: (sender: any, options: any) => void) => {
              try {
                // Call handler with survey as first param (sender) and options as second
                handler(survey, uploadOptions);
              } catch (error) {
                console.error("Error calling upload handler:", error);
              }
            },
          );
        } else {
          // Method 2: Try calling fire/notify methods if available
          if (typeof (survey.onUploadFiles as any).fire === "function") {
            (survey.onUploadFiles as any).fire(survey, uploadOptions);
          } else if (
            typeof (survey.onUploadFiles as any).notify === "function"
          ) {
            (survey.onUploadFiles as any).notify(survey, uploadOptions);
          } else {
            // Method 3: Try to get callbacks from the event
            const callbacks = (survey.onUploadFiles as any)?.callbacks || [];
            callbacks.forEach(
              (callback: (sender: any, options: any) => void) => {
                try {
                  callback(survey, uploadOptions);
                } catch (error) {
                  console.error("Error calling upload callback:", error);
                }
              },
            );
          }
        }
      } else {
        console.warn("Survey or onUploadFiles event not available");
      }
    }

    // Reset inputs
    if (this.fileInputRef.current) {
      this.fileInputRef.current.value = "";
    }
    if (this.directoryInputRef.current) {
      this.directoryInputRef.current.value = "";
    }
    if (this.cameraInputRef.current) {
      this.cameraInputRef.current.value = "";
    }
  };

  updateProgress = (
    fileName: string,
    progress: number,
    status: UploadProgress["status"],
    error?: string,
  ) => {
    // Check if the file still exists in the question value before updating progress
    // This prevents race conditions where a file is removed while upload is in progress
    const currentValue = this.value || [];
    const fileExists = currentValue.some((item: any) => {
      if (item instanceof File) {
        return item.name === fileName;
      }
      const surveyFile = item as SurveyJSFileObject;
      const itemFileName =
        surveyFile.name ||
        surveyFile.file?.name ||
        (surveyFile.file instanceof File ? surveyFile.file.name : undefined);
      return itemFileName === fileName;
    });

    // If file doesn't exist and we're not in an error state, don't update progress
    // Allow error updates even if file was removed (to show the error)
    if (!fileExists && status !== "error") {
      return;
    }

    const currentProgress = this.state.uploadProgress || [];
    const existingIndex = currentProgress.findIndex(
      (p: UploadProgress) => p.fileName === fileName,
    );

    const newProgress: UploadProgress = {
      error,
      fileName,
      progress,
      status,
    };

    let updatedProgress: UploadProgress[];
    if (existingIndex >= 0) {
      updatedProgress = [...currentProgress];
      updatedProgress[existingIndex] = newProgress;
    } else {
      updatedProgress = [...currentProgress, newProgress];
    }

    // Always update state to trigger re-render and show progress
    this.setState({ uploadProgress: updatedProgress });
  };

  removeFile = (fileName: string) => {
    const currentValue = this.value || [];
    // Remove file by matching filename in various possible formats
    const newValue = currentValue.filter((item: any) => {
      if (item instanceof File) {
        return item.name !== fileName;
      }
      // SurveyJS file object format
      const surveyFile = item as SurveyJSFileObject;
      const itemFileName =
        surveyFile.name ||
        surveyFile.file?.name ||
        (surveyFile.file instanceof File ? surveyFile.file.name : undefined);
      return itemFileName !== fileName;
    });
    this.question.value = newValue;

    // This question extends `Question`, not `QuestionFile`, so survey-core never
    // fires `onClearFiles` for it — the page's handler, which owns the
    // attachment-id map that becomes `attachments.ids`, has to be invoked by
    // hand. Skipping it leaves the removed attachment in that map, so it keeps
    // `uploaded = true` and is still exported. `currentValue` is the
    // pre-removal list, which is what the handler matches `fileName` against.
    const survey = this.question.survey as any;
    survey?.clearFiles?.(
      this.question,
      this.question.name,
      currentValue,
      fileName,
      () => {},
    );

    // Update progress - remove progress tracking for this file
    // Use setState callback to ensure we're working with the latest state
    this.setState(
      (prevState: { uploadProgress: UploadProgress[] } | undefined) => {
        const currentProgress = prevState?.uploadProgress || [];
        const updatedProgress = currentProgress.filter(
          (p: UploadProgress) => p.fileName !== fileName,
        );
        return { uploadProgress: updatedProgress };
      },
    );
  };

  handleExportStatusChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    fileItem: SurveyJSFileObject,
  ) => {
    const includeInExport = event.target.checked;

    if (!fileItem.id || !fileItem.submission_id) {
      console.error(
        "File item missing required properties for export status update",
      );
      toast.error(tQuestion("enhancedFileUpload.exportStatusMissingInfo"));
      return;
    }

    try {
      await postSubmissionAttachmentExportStatus({
        path: {
          submission_id: fileItem.submission_id,
          attachment_id: fileItem.id,
        },
        body: { include_in_export: includeInExport },
      });

      // Update local state to reflect the change
      const currentValue = (this.question.value || []) as any[];
      const updatedValue = currentValue.map((item) => {
        if (item instanceof File) {
          return item;
        }
        const surveyFile = item as SurveyJSFileObject;
        if (surveyFile.id === fileItem.id) {
          return { ...surveyFile, include_in_export: includeInExport };
        }
        return item;
      });
      this.question.value = updatedValue;
      this.setState({});
    } catch (error) {
      console.error("Failed to update export status:", error);
      toast.error(tQuestion("enhancedFileUpload.exportStatusFailed"));
    }
  };

  downloadFile = async (fileItem: SurveyJSFileObject) => {
    const fileName = fileItem.name || "file";

    // Prevent multiple simultaneous downloads of the same file
    if (this.state.downloadingFiles?.has(fileName)) {
      return;
    }

    try {
      // Mark file as downloading
      this.setState((prevState: any) => ({
        downloadingFiles: new Set(prevState.downloadingFiles || []).add(
          fileName,
        ),
      }));

      // Initialize download progress
      this.updateProgress(fileName, 0, "downloading");

      const data = await axios.get(fileItem.content || "", {
        responseType: "blob",
        headers: {
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
          Expires: "0",
        },
        onDownloadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total,
            );
            this.updateProgress(fileName, percentCompleted, "downloading");
          } else {
            // If total is not available, show indeterminate progress
            this.updateProgress(fileName, 50, "downloading");
          }
        },
      });

      // Create a URL for the blob
      const blobUrl = window.URL.createObjectURL(data.data);

      // Create and trigger download link
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();

      // Clean up
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);

      // Mark download as completed
      this.updateProgress(fileName, 100, "completed");

      // Remove from downloading set after a short delay to allow UI to show completion
      setTimeout(() => {
        this.setState((prevState: any) => {
          const newSet = new Set(prevState.downloadingFiles || []);
          newSet.delete(fileName);
          return { downloadingFiles: newSet };
        });
        // Clear progress after download completes
        const currentProgress = this.state.uploadProgress || [];
        const updatedProgress = currentProgress.filter(
          (p: UploadProgress) =>
            p.fileName !== fileName || p.status !== "downloading",
        );
        this.setState({ uploadProgress: updatedProgress });
      }, 500);
    } catch (error) {
      console.error("Failed to download file:", error);
      toast.error(tQuestion("enhancedFileUpload.downloadFileFailed"));
      this.updateProgress(
        fileName,
        0,
        "error",
        tQuestion("enhancedFileUpload.downloadFailed"),
      );

      // Remove from downloading set on error
      this.setState((prevState: any) => {
        const newSet = new Set(prevState.downloadingFiles || []);
        newSet.delete(fileName);
        return { downloadingFiles: newSet };
      });
    }
  };

  renderUploadBox = (
    label: string,
    inputRef: React.RefObject<HTMLInputElement>,
    isDirectory: boolean = false,
    isCamera: boolean = false,
  ) => {
    const { acceptedTypes, allowDirectoryUpload } = this.question;
    const inputProps = isDirectory
      ? {
          directory: "",
          multiple: true,
          webkitdirectory: "",
        }
      : isCamera
        ? {
            accept: "image/*",
            capture: "environment" as const,
            multiple: false,
          }
        : {
            accept: acceptedTypes,
            multiple: true,
          };

    return (
      <Box
        component="label"
        sx={{
          "&:hover": this.props.isDisplayMode
            ? {}
            : {
                borderColor: colors.primary.main,
              },
          alignItems: "center",
          backgroundColor: this.props.isDisplayMode
            ? undefined
            : "var(--cr-fill, #f5f5f5)",
          border: "2px dashed var(--cr-border, #d0d0d0)",
          borderRadius: "var(--cr-radius, 4px)",
          cursor: this.props.isDisplayMode ? "default" : "pointer",
          display: "flex",
          flex: allowDirectoryUpload ? 1 : "none",
          flexDirection: "column",
          gap: 1,
          justifyContent: "center",
          minHeight: "120px",
          opacity: this.props.isDisplayMode ? 0.6 : 1,
          p: 3,
          position: "relative",
          transition: "all 0.2s ease",
        }}
      >
        <input
          ref={inputRef}
          type="file"
          style={{ display: "none" }}
          onChange={this.handleFileChange}
          {...inputProps}
        />
        {isCamera ? (
          <CameraAlt
            sx={{ color: "var(--cr-disabled-color, #9e9e9e)", fontSize: 48 }}
          />
        ) : isDirectory ? (
          <Folder
            sx={{ color: "var(--cr-disabled-color, #9e9e9e)", fontSize: 48 }}
          />
        ) : (
          <CloudUpload
            sx={{ color: "var(--cr-disabled-color, #9e9e9e)", fontSize: 48 }}
          />
        )}
        <Typography color="text.secondary" variant="body2">
          {label}
        </Typography>
      </Box>
    );
  };

  renderFilePreview = (
    item: FileWithPreview | SurveyJSFileObject,
    _: number,
  ) => {
    // Extract file info from either File object or SurveyJS file object
    const file = this.getFileInfo(item);
    if (!file) {
      return null;
    }

    const surveyFile = item as SurveyJSFileObject;
    // Extract filename from various possible locations
    // Priority: surveyFile.name (explicit name) > file.name (from File object) > fileObj.name > preferredName
    const fileObj = surveyFile.file as File | undefined;
    const preferredName = (file as any)?.preferredName;
    const fileName =
      surveyFile.name ||
      preferredName ||
      file.name ||
      (fileObj ? fileObj.name : undefined) ||
      tQuestion("enhancedFileUpload.unknownFile");
    const fileType =
      file.type ||
      surveyFile.type ||
      (fileObj ? fileObj.type : undefined) ||
      "";
    const preview =
      file.preview ||
      surveyFile.thumbnail_url ||
      surveyFile.content ||
      (fileObj ? (fileObj as FileWithPreview).preview : undefined) ||
      "";

    const hasPreview =
      preview ||
      (fileType?.startsWith("image/") &&
        !fileType.includes("heic") &&
        !fileType.includes("heif") &&
        !fileName.toLowerCase().endsWith(".heic") &&
        !fileName.toLowerCase().endsWith(".heif"));

    const progress = (this.state.uploadProgress || []).find(
      (p: UploadProgress) => p.fileName === fileName,
    );

    // Check if file is currently downloading
    const isDownloading = this.state.downloadingFiles?.has(fileName) || false;

    // Determine if we should show progress bar
    const showProgress =
      progress &&
      (progress.status === "uploading" ||
        progress.status === "processing" ||
        progress.status === "downloading" ||
        (progress.status === "completed" && progress.progress < 100));
    const showError = progress && progress.status === "error";

    // Use a stable key based on fileName only (not index) to prevent React
    // from getting confused when files are removed and indices change
    // If fileName is not unique, add a unique identifier from the file object
    const fileId =
      (item as SurveyJSFileObject).id ||
      (item instanceof File
        ? `${fileName}-${file.size}-${file.lastModified}`
        : fileName);

    // Check if we should show the export checkbox (display mode and file has required properties)
    const showExportCheckbox =
      this.props.isDisplayMode &&
      !(item instanceof File) &&
      (item as SurveyJSFileObject).id &&
      (item as SurveyJSFileObject).submission_id;

    return (
      <Box
        key={fileId}
        sx={{
          border: "1px solid var(--divider)",
          borderRadius: "var(--cr-radius, 4px)",
          mb: 1,
          overflow: "hidden",
          position: "relative",
        }}
      >
        {showExportCheckbox && (
          <Box sx={{ p: 1, pb: 0 }}>
            <Tooltip
              title={tQuestion("enhancedFileUpload.rmsExportTooltip")}
              arrow
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={
                      (item as SurveyJSFileObject).include_in_export || false
                    }
                    onChange={(e) =>
                      this.handleExportStatusChange(
                        e,
                        item as SurveyJSFileObject,
                      )
                    }
                    color="primary"
                    onClick={(e) => e.stopPropagation()}
                  />
                }
                label={tQuestion("enhancedFileUpload.rmsExportLabel")}
                onClick={(e) => e.stopPropagation()}
              />
            </Tooltip>
          </Box>
        )}
        <Box
          onClick={() => {
            if (isDownloading) return;
            const isPreviewable =
              fileType?.startsWith("image/") || fileType?.startsWith("video/");
            if (isPreviewable && preview) {
              this.setState({ previewItem: item });
            } else {
              this.downloadFile(item);
            }
          }}
          sx={{
            alignItems: "center",
            cursor: isDownloading ? "wait" : "pointer",
            display: "flex",
            gap: 1,
            opacity: isDownloading ? 0.7 : 1,
            p: 1,
            pointerEvents: isDownloading ? "none" : "auto",
            "&:hover": {
              backgroundColor: isDownloading ? undefined : colors.action.hover,
            },
          }}
        >
          {hasPreview && preview ? (
            <Box
              component="img"
              src={preview}
              alt={fileName}
              sx={{
                borderRadius: "var(--cr-radius, 4px)",
                height: "60px",
                objectFit: "cover",
                width: "60px",
              }}
            />
          ) : (
            <Box
              sx={{
                alignItems: "center",
                backgroundColor: "var(--cr-fill, #f5f5f5)",
                borderRadius: "var(--cr-radius, 4px)",
                display: "flex",
                height: "60px",
                justifyContent: "center",
                width: "60px",
              }}
            >
              <InsertDriveFile
                sx={{ color: colors.text.disabled, fontSize: 32 }}
              />
            </Box>
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              noWrap
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              variant="body2"
            >
              {fileName}
            </Typography>
            {file.size && (
              <Typography color="text.secondary" variant="caption">
                {(file.size / 1024).toFixed(1)} KB
              </Typography>
            )}
            {!file.size && (item as SurveyJSFileObject).file?.size && (
              <Typography color="text.secondary" variant="caption">
                {((item as SurveyJSFileObject).file!.size / 1024).toFixed(1)} KB
              </Typography>
            )}
          </Box>
          {!this.props.isDisplayMode && (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                this.removeFile(fileName);
              }}
              size="small"
              sx={{ color: colors.error.main }}
            >
              ×
            </IconButton>
          )}
        </Box>
        {/* Always show progress bar if file is uploading/processing/downloading */}
        {showProgress && progress && (
          <Box sx={{ p: 1, pt: 0 }}>
            <ProgressBar
              progress={progress.progress}
              fileName={
                progress.status === "processing"
                  ? tQuestion("enhancedFileUpload.processing")
                  : progress.status === "downloading"
                    ? tQuestion("enhancedFileUpload.downloading")
                    : undefined
              }
            />
          </Box>
        )}
        {showError && progress && (
          <Box sx={{ bgcolor: colors.action.selected, p: 1, pt: 0 }}>
            <Typography color="error" variant="caption">
              {progress.error || tQuestion("enhancedFileUpload.uploadFailed")}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  // Check if device supports camera
  isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    );
  };

  renderElement() {
    const { allowDirectoryUpload } = this.question;
    const isMobile = this.isMobileDevice();

    return (
      <Box sx={{ width: "100%" }}>
        {/* Upload boxes */}
        {!this.props.isDisplayMode && (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              mb: 2,
            }}
          >
            {this.renderUploadBox(
              tQuestion("enhancedFileUpload.selectFiles"),
              this.fileInputRef,
              false,
            )}
            {isMobile && (
              <Box
                component="label"
                sx={{
                  "&:hover": this.props.isDisplayMode
                    ? {}
                    : {
                        borderColor: colors.primary.main,
                      },
                  alignItems: "center",
                  backgroundColor: this.props.isDisplayMode
                    ? undefined
                    : colors.action.hover,
                  border: "2px dashed var(--divider)",
                  borderRadius: "4px",
                  cursor: this.props.isDisplayMode ? "default" : "pointer",
                  display: "flex",
                  flex: allowDirectoryUpload ? 1 : "none",
                  flexDirection: "column",
                  gap: 1,
                  justifyContent: "center",
                  minHeight: "120px",
                  opacity: this.props.isDisplayMode ? 0.6 : 1,
                  p: 3,
                  position: "relative",
                  transition: "all 0.2s ease",
                }}
              >
                <input
                  ref={this.cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  style={{ display: "none" }}
                  onChange={this.handleFileChange}
                />
                <CameraAlt
                  sx={{
                    color: "var(--cr-disabled-color, #9e9e9e)",
                    fontSize: 48,
                  }}
                />
                <Typography color="text.secondary" variant="body2">
                  {tQuestion("enhancedFileUpload.takeAPhoto")}
                </Typography>
              </Box>
            )}
            {allowDirectoryUpload &&
              this.renderUploadBox(
                tQuestion("enhancedFileUpload.selectFolder"),
                this.directoryInputRef,
                true,
              )}
          </Box>
        )}

        {/* Display uploaded files with previews */}
        {this.value && this.value.length > 0 && (
          <Box sx={{ mt: 2 }}>
            {Array.isArray(this.value) &&
              this.value
                .map(
                  (item: FileWithPreview | SurveyJSFileObject, index: number) =>
                    this.renderFilePreview(item, index),
                )
                .filter((element) => element !== null)}
          </Box>
        )}

        {/* Attachment preview modal */}
        {this.state.previewItem &&
          (() => {
            const previewSurveyFile = this.state
              .previewItem as SurveyJSFileObject;
            const previewFile = this.getFileInfo(this.state.previewItem);
            const previewSrc =
              previewFile?.preview || previewSurveyFile.content || "";
            const previewType =
              previewFile?.type || previewSurveyFile.type || "";
            const previewName =
              previewSurveyFile.name || previewFile?.name || "";

            return (
              <AttachmentPreviewModal
                open={!!this.state.previewItem}
                onClose={() => this.setState({ previewItem: null })}
                mediaSrc={previewSrc}
                mediaType={
                  previewType?.startsWith("video/") ? "video" : "image"
                }
                fileName={previewName}
                onDownload={() => {
                  this.downloadFile(
                    this.state.previewItem as SurveyJSFileObject,
                  );
                  this.setState({ previewItem: null });
                }}
              />
            );
          })()}
      </Box>
    );
  }
}

// Register the React component for rendering
ReactQuestionFactory.Instance.registerQuestion(
  ENHANCED_FILE_UPLOAD_TYPE,
  (props) => {
    return createElement(SurveyQuestionEnhancedFileUpload, props);
  },
);

// Register property grid editor
PropertyGridEditorCollection.register({
  fit: function (prop: { type: string }) {
    return prop.type === "enhanced-file-upload";
  },
  getJSON: function () {
    return {
      acceptedTypes: "",
      allowDirectoryUpload: false,
      maxSize: -1,
      type: ENHANCED_FILE_UPLOAD_TYPE,
    };
  },
});
